import React, { useId } from "react";
import { Iconography, IconographySizes } from "@hid-galaxy-ui/galaxy-react";
import { useAppDispatch } from "../../../../../../../redux";
import { getFileUrl } from "../../../../../../../redux/saga/partner-program/calculator/saga-action";

interface CommentsFilesSectionProps {
  isEditable: boolean;
  selectedAssesmentData: any;
  comment?: string;
  onCommentChange?: (v: string) => void;
  existingDocuments?: string[];
  newFiles?: File[];
  onAddFiles?: (files: File[]) => void;
  onRemoveNewFile?: (index: number) => void;
  onRemoveExistingDocument?: (index: number) => void;
}

const CommentsFilesSection: React.FC<CommentsFilesSectionProps> = ({
  isEditable,
  selectedAssesmentData,
  comment: controlledComment,
  onCommentChange,
  existingDocuments,
  newFiles = [],
  onAddFiles,
  onRemoveNewFile,
  onRemoveExistingDocument,
}) => {
  const dispatch = useAppDispatch();
  const fileInputId = useId();

  const commentText =
    controlledComment !== undefined
      ? controlledComment
      : selectedAssesmentData?.comments?.[0]?.comment ?? "";

  const documents =
    existingDocuments !== undefined
      ? existingDocuments
      : selectedAssesmentData?.documents ?? [];

  const hasDocuments =
    Array.isArray(documents) && documents.length > 0;

  const downloadFileUrlFn = (fileName: any, fileType: any) => {
    dispatch(getFileUrl(fileName, fileType));
  };

  if (isEditable) {
    return (
      <>
        <h2 className="section-title">Add Comments & Files (Optional)</h2>
        <div className="comments-files-grid">
          <div className="comment-wrapper">
            <div className="comment-header">
              <label htmlFor={`${fileInputId}-comment`}>
                Comment (Optional)
              </label>
            </div>
            <textarea
              id={`${fileInputId}-comment`}
              placeholder="Comment"
              value={commentText}
              onChange={(e) =>
                onCommentChange?.(e.target.value.slice(0, 500))
              }
              rows={4}
            />
          </div>

          <div className="files-wrapper">
            {onAddFiles && (
              <>
                <label
                  htmlFor={fileInputId}
                  className="add-files-btn"
                >
                  ADD FILES
                </label>
                <input
                  id={fileInputId}
                  type="file"
                  multiple
                  style={{ display: "none" }}
                  onChange={(e) => {
                    if (e.target.files?.length) {
                      onAddFiles(Array.from(e.target.files));
                      e.target.value = "";
                    }
                  }}
                />
              </>
            )}

            <div className="file-list">
              {documents.map((doc: any, index: number) => (
                <div key={`ex-${index}`} className="file-item">
                  <span className="file-name">{doc}</span>
                  {onRemoveExistingDocument && (
                    <button
                      type="button"
                      className="delete-icon-btn"
                      onClick={() => onRemoveExistingDocument(index)}
                      aria-label="Remove file"
                    >
                      <Iconography
                        icon="xmark"
                        size={IconographySizes.Small}
                        className="delete-icon"
                      />
                    </button>
                  )}
                </div>
              ))}
              {newFiles.map((file, index) => (
                <div key={`new-${index}`} className="file-item">
                  <span className="file-name">{file.name}</span>
                  {onRemoveNewFile && (
                    <button
                      type="button"
                      className="delete-icon-btn"
                      onClick={() => onRemoveNewFile(index)}
                      aria-label="Remove new file"
                    >
                      <Iconography
                        icon="xmark"
                        size={IconographySizes.Small}
                        className="delete-icon"
                      />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <h2>Comments & Files</h2>
      <div
        className={[
          "comments-files-grid",
          "comments-files-grid--readonly",
          !hasDocuments && "comments-files-grid--no-files",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <div className="comment-section">
          <label>Comment</label>
          <span className="char-count">{commentText.length}/500</span>
          <div className="form-value comment-value">{commentText}</div>
        </div>
        {hasDocuments && (
          <div className="files-section">
            {documents.map((doc: any, index: number) => (
              <div
                key={`ex-${index}`}
                className="file-item file-item--readonly"
              >
                <a
                  onClick={() => {
                    downloadFileUrlFn(doc, "view");
                  }}
                  className="document-item"
                >
                  <span>{doc}</span>
                  <Iconography icon="eye" size={IconographySizes.Small} />
                </a>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default CommentsFilesSection;
